import { useState } from "react";
import { IoClose, IoNotificationsOutline, IoNotificationsOffOutline } from "react-icons/io5";
import { MdOutlineImage, MdOutlineInsertDriveFile, MdOutlinePeople } from "react-icons/md";
import Api from "../../service/http";

const Details = ({ chat, setChat, showDetails, setShowDetails }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);

  const handleImageClick = (image) => {
    setSelectedMessage(image);
    setIsOpen(true);
  };

  const handleClose = () => {
    setSelectedMessage(null);
    setIsOpen(false);
  };

  const [isZoomed, setIsZoomed] = useState(false);

  const handleDoubleClick = () => {
    setIsZoomed(prev => !prev);
  };

  const toggleNotifications = async () => {
    await Api.get(`/web/chat/${chat?.id}/${!chat?.mute ? 'mute' : 'unmute'}`);
    setChat(prevChat => ({
      ...prevChat,
      mute: !prevChat?.mute
    }));
  }

  return (
    <aside className={`${showDetails ? 'fixed inset-0 z-50 lg:static lg:w-[300px]' : 'fixed inset-y-0 right-0 w-0 opacity-0 lg:static lg:w-0 lg:opacity-100 overflow-hidden border-l-0 lg:border-l-0'} shrink-0 h-full flex flex-col items-center lg:items-stretch border-l transition-all duration-300 ease-in-out bg-white border-gray-100`}>
      <div className="w-full max-w-[400px] lg:w-[300px] flex-1 overflow-y-auto custom-scrollbar p-6 pt-10 bg-white">
        <div className="flex justify-end lg:hidden absolute top-4 right-4">
          <button onClick={() => showDetails && typeof setShowDetails === 'function' && setShowDetails(false)} className="p-2 rounded-full hover:bg-gray-100 text-gray-800">
            <IoClose size={24} />
          </button>
        </div>
        <div className="flex flex-col items-center text-center mb-8">
          <div className="relative mb-4">
            <img
              src={chat?.image || "https://placehold.co/400"}
              className="w-20 h-20 rounded-full object-cover shadow-md"
              alt="profile"
            />
            <div className="absolute bottom-1 right-1 w-4 h-4 rounded-full border-2 border-white bg-green-500"></div>
          </div>
          <h4 className="text-lg font-medium mb-1 text-gray-800">{chat?.name}</h4>
          <p className="text-sm text-gray-500">Member</p>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-center gap-4 mb-8">
          <button 
            onClick={toggleNotifications}
            className="flex flex-col items-center gap-1 group"
          >
            <div className={`p-2 rounded-lg transition-all ${
              chat?.mute 
                ? 'bg-red-50 text-red-500' 
                : 'bg-gray-50 text-gray-500'
            } group-hover:bg-indigo-50 group-hover:text-indigo-600`}>
              {chat?.mute ? <IoNotificationsOffOutline size={20} /> : <IoNotificationsOutline size={20} />}
            </div>
            <span className="text-[10px] uppercase font-bold tracking-wider text-gray-400 group-hover:text-indigo-600">Mute</span>
          </button>
        </div>

        {/* Sections */}
        <div className="space-y-6 text-left">
          {/* Media Section */}
          <div>
            <div className="flex items-center gap-2 mb-3 text-gray-400">
              <MdOutlineImage size={18} />
              <span className="text-xs font-bold uppercase tracking-widest">Media</span>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {chat?.messages?.filter(message => ['IMGS', 'IMG', 'VID'].includes(message?.message_type))?.reverse()?.slice(0, 9).map((message, index) => {
                if (message?.message_type === 'IMGS') {
                  return message?.media?.map((item, i) => (
                    <img
                      key={`${index}-${i}`}
                      src={item?.file || "https://placehold.co/100"}
                      alt="media"
                      className="w-full h-20 object-cover rounded-lg cursor-pointer hover:opacity-80 transition-opacity shadow-sm"
                      onClick={() => handleImageClick(message)}
                    />
                  ));
                }
                if (message?.message_type === 'IMG') {
                  return (
                    <img
                      key={index}
                      src={message?.media?.file || "https://placehold.co/100"}
                      alt="media"
                      className="w-full h-20 object-cover rounded-lg cursor-pointer hover:opacity-80 transition-opacity shadow-sm"
                      onClick={() => handleImageClick(message)}
                    />
                  );
                }
                return (
                  <div key={index} className="relative w-full h-20 rounded-lg overflow-hidden group cursor-pointer" onClick={() => handleImageClick(message)}>
                    <video
                      src={message?.media?.file}
                      className="w-full h-full object-cover"
                      muted
                      preload="metadata"
                    />
                    <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="w-8 h-8 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-white">
                        <div className="w-0 h-0 border-t-4 border-t-transparent border-l-8 border-l-white border-b-4 border-b-transparent ml-1"></div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Documents Section */}
          <div>
            <div className="flex items-center gap-2 mb-3 text-gray-400">
              <MdOutlineInsertDriveFile size={18} />
              <span className="text-xs font-bold uppercase tracking-widest">Documents</span>
            </div>
            <div className="space-y-2">
              {chat?.messages?.filter(message => !['IMG', 'TXT', 'VID', 'AUD'].includes(message?.message_type) && message?.media?.file)?.reverse()?.slice(0, 5).map((message, index) => (
                <a
                  key={index}
                  href={message?.media?.file}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 p-2 rounded-lg border border-gray-100 hover:bg-gray-50 transition-all"
                >
                  <div className="w-8 h-8 rounded bg-indigo-50 flex items-center justify-center text-indigo-500 shrink-0">
                    <MdOutlineInsertDriveFile size={18} />
                  </div>
                  <div className="min-w-0 flex-1 text-left">
                    <p className="text-xs font-medium truncate text-gray-700">
                      {message?.media?.name || "Document"}
                    </p>
                    <p className="text-[10px] text-gray-400">File</p>
                  </div>
                </a>
              ))}
            </div>
          </div>

          {/* Members Section */}
          {chat?.users?.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3 text-gray-400">
                <MdOutlinePeople size={18} />
                <span className="text-xs font-bold uppercase tracking-widest">Members</span>
              </div>
              <div className="space-y-3">
                {chat?.users?.map((user, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <img
                      src={user?.user?.dp ?? "https://placehold.co/400"}
                      alt="User"
                      className="w-8 h-8 rounded-full border border-gray-100 shadow-sm object-cover"
                    />
                    <div className="flex-1 min-w-0 text-left">
                      <p className="text-xs font-medium text-gray-700">
                        {user?.user?.first_name || "User"}
                      </p>
                      <p className="text-[10px] text-gray-400">Participant</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {isOpen && (
        <div
          onClick={(e) => { if (!e.target.closest(".modal-content")) handleClose(); }}
          className="fixed inset-0 bg-black/90 flex items-center justify-center z-[100] p-4 backdrop-blur-sm"
        >
          <div className="relative max-w-5xl w-full flex flex-col items-center">
            <button 
              onClick={handleClose}
              className="absolute -top-12 right-0 text-white hover:text-indigo-400 transition-colors"
            >
              <IoClose size={32} />
            </button>
            
            {['IMG', 'IMGS'].includes(selectedMessage?.message_type) ? (
              <img
                src={(selectedMessage?.message_type === 'IMGS' ? selectedMessage?.media?.[0]?.file : selectedMessage?.media?.file) || "https://placehold.co/400"}
                alt="Zoomed"
                onDoubleClick={handleDoubleClick}
                className={`modal-content max-h-[85vh] object-contain rounded-lg shadow-2xl transition-transform duration-300 ${
                  isZoomed ? "scale-150" : "scale-100"
                }`}
              />
            ) : (
              <video
                src={selectedMessage?.media?.file}
                controls
                autoPlay
                className="modal-content max-h-[85vh] w-full rounded-lg shadow-2xl"
              />
            )}
          </div>
        </div>
      )}
    </aside>
  );
}

export default Details;